/* API module for AuthenticationController */

import { api } from './http';
import { toApiError } from './errors';
import type { AuthLoginRequestDTO, AuthLoginResponseDTO, VerifyPinRequestDTO, VerifyPinResponseDTO } from './types';

export async function login(payload: AuthLoginRequestDTO): Promise<AuthLoginResponseDTO> {
  try {
    const { data } = await api.post<AuthLoginResponseDTO, AuthLoginRequestDTO>(
      'Authentication/login',
      payload
    );

    // Only store token if PIN verification is not required
    const token = (data as any)?.token as string | undefined;
    if (token && !data.requiresPinVerification) {
      storeTokenAndExpiration(token, (data as any)?.expiration);
    }

    return data;
  } catch (e: any) {
    // Backend returns 400 with error details in the response body
    // Extract the error response if available
    if (e?.data && typeof e.data === 'object') {
      return e.data as AuthLoginResponseDTO;
    }
    throw toApiError(e);
  }
}

export async function verifyPin(payload: VerifyPinRequestDTO): Promise<VerifyPinResponseDTO> {
  try {
    const { data } = await api.post<VerifyPinResponseDTO, VerifyPinRequestDTO>(
      'Authentication/verify-pin',
      payload
    );

    const token = (data as any)?.token as string | undefined;
    if (token) {
      storeTokenAndExpiration(token, (data as any)?.expiration);
    }

    return data;
  } catch (e: any) {
    // Backend returns 400 with error details in the response body
    // Extract the error response if available
    if (e?.data && typeof e.data === 'object') {
      return e.data as VerifyPinResponseDTO;
    }
    throw toApiError(e);
  }
}

export async function refreshToken(): Promise<VerifyPinResponseDTO> {
  try {
    const { data } = await api.post<VerifyPinResponseDTO, Record<string, never>>(
      'Authentication/refresh',
      {} as Record<string, never>
    );

    const token = (data as any)?.token as string | undefined;
    if (token) {
      storeTokenAndExpiration(token, (data as any)?.expiration);
    }

    return data;
  } catch (e: any) {
    if (e?.data && typeof e.data === 'object') {
      return e.data as VerifyPinResponseDTO;
    }
    throw toApiError(e);
  }
}

/** Speichert Token und Ablaufzeitpunkt (als ISO-String) im localStorage */
function storeTokenAndExpiration(token: string, expiration?: string): void {
  try {
    localStorage.setItem('accessToken', token);
    if (expiration) {
      localStorage.setItem('accessTokenExpiration', expiration);
    }
  } catch {}
}

export function getAccessTokenExpiration(): Date | null {
  try {
    const raw = localStorage.getItem('accessTokenExpiration');
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

export function logout(): void {
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('accessTokenExpiration');
  } catch {}
}

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
}
